import { ApprovalStatuses } from '@/enums';
import { RepairStatuses } from '@/enums/statuses/repairs';
import {
  ApiPagination, Filters, RequestPagination, SimpleEnumType,
} from '@/types';
import { Clients } from '@/modules/clients/types/';
import { Organizations } from '@/modules/organizations/types';
import { ReturnZipsStatuses } from '@/enums/statuses/return-zips';
import { Products } from '@/modules/products/types';

export namespace Repairs {
  export enum Pages {
    REPAIRS = 'repairs',
    REPAIR_DETAIL = 'repair',
    NEW_REPAIR = 'new_repair',
    WARRANTY_CHECK = 'warranty-check',
    RETURN_ZIPS = 'return-zips',
    ORDER_SIDES = 'order_sides',
    WARRANTY = 'warranty',
    EDIT_MAIN_INFO = 'main_edit',

    RETURN_ZIPS_ALERT = 'returned_zip'
  }

  // operation_type: SimpleEnumType<RepairOperationType>;
  export interface Repair {
    [key: string]: unknown;
    id: number;
    client_name: string | null;
    status: SimpleEnumType<RepairStatuses>;
    serial_number: string | null;
    nomenclature: string | null;
    organization?: Organizations.Organization;
    operation_type: string | null;
    repair_type: string | null;
    create_at?: string | null;
    approval_status?: SimpleEnumType<ApprovalStatuses> | null;
    complections?: [] | null;
    end_at?: string | null;
    has_unread_change: boolean;
  }

  export interface Category {
    id: number | string;
    name: string | null;
  }

  export interface OperationType {
    id: number | string;
    name: string | null;
  }
  export interface ComponentPayload {
    nomenclature: string | null;
    comment: string | null;
  }
  export interface NewRepair {
    [key: string]: unknown;
    id?: number;
    client_id: number;
    organization_id: number;
    equipment_category_id: number;
    operation_type_id: number;
    nomenclature: string;
    serial_number: string;
    malfunction: string;
    complections: ComponentPayload[];
    files: File[];
    expired_date: string;
    is_product_not_found: boolean
  }
  export interface DetailedInfo {
    [key: string]: unknown;
    id: number;
    approval_status: string | null;
    client: Clients.Client | null;
    company: Organizations.Organization | null;
    create_at: string | null;
    end_at?: string | null;
    accepted_at?: string | null;
    nomenclature: string | null;
    operation_type: { id: number; name: string } | null;
    organization: Organizations.Organization | null;
    repair_type: string | null;
    serial_number: string | null;
    contract: string | null;
    status: SimpleEnumType<RepairStatuses> | null;
    is_available_editing: boolean,
    organization_id: number;
    expired_date: string | null,
    operation_type_id: number | null;
    work_price_rub: number | string | null;
    operation_type_slug: string | null;
    contact_person: string | null
  }

  export interface Component {
    [key: string]: unknown;
    id: number | string;
    name: string | null;
    comment: string | null;
  }

  export interface Diagnostics {
    [key: string]: unknown;
    comment: string | null;
    conclusion: string | null;
    condition_code_type: { id?: number; name?: string } | null;
    equipment_category: { id?: number; name?: string } | null;
    malfunction: string | null;
    operation_type: { id?: number; name?: string; slug?: string } | null;
    repair_type: { id?: number; name?: string } | null;
    symptom_type: { id?: number; name?: string } | null;
    work_price_rub: number | null;
  }

  export interface DiagnosticsSelect {
    symptom_types: { id: number | string; name: string }[];
    repair_types: { id: number | string; name: string }[];
    operation_types: { id: number | string; name: string }[];
    equipment_categories: { id: number | string; name: string }[];
    condition_code_types: { id: number | string; name: string }[];
  }

  export interface DiagnosticsPayload {
    comment: string | null;
    conclusion: string | null;
    condition_code_id: number | null;
    equipment_category_id: number | null;
    malfunction: string | null;
    operation_type_id: number | null;
    repair_type_id: number | null;
    symptom_type_id: number | null;
    work_price_rub: number | null;
  }

  export interface Stage {
    [key: string]: unknown;
    id: number;
    status: SimpleEnumType<RepairStatuses>;
    full_name: string | null;
    comment: string | null;
  }

  export interface StagePayload {
    status: RepairStatuses;
    comment?: string
  }
  export interface Document {
    id: number,
    name: string | null,
    created_at: string | null,
  }

  export interface ProductDetails {
    id: number;
    nomenclature: string;
    product_code?: number | string;
  }
  export interface Product {
    id: number;
    full_price: number | null;
    catalogue_price: string | number | null;
    price_set: string | number | null;
    product: ProductDetails;
    quantity: string | number | null;
    serial_number: string | number | null;
  }

  export interface AddProductPayload {
    product_id: number;
    price?: number | null | string;
  }

  export type UpdateProductPayload = Pick<Product, 'quantity' | 'serial_number'> & {
    price: string | number | null;
  };

  // operation_type_id: RepairOperationType | null;
  export interface RepairsInfoFilters extends Filters {
    [key: string]: unknown;
    client_id: string | null;
    repair_type_id: string | null;
    status: RepairStatuses | null;
    date: string[] | null;
    end_date: string[] | null;
    is_approved: boolean | null;
    operation_type_id: string | null;
    only_has_unread_changes: boolean;
  }

  export interface ExtendedWarranty {
    id: number,
    type: string,
    date_start: string,
    date_end: string,
    actual: boolean,
  }

  export interface Warranty {
    [key: string]: unknown;
    expired_date: string;
    id: number;
    nomenclature: string;
    number: string;
    product_id: number;
    extended_warranties: ExtendedWarranty[]
  }

  export interface ReturnZipsFilters {
    [key: string]: unknown;
    search: string;
    date: string | null;
    status: string | null;
  }

  export interface ReturnZip {
    id: number;
    order_side_id: number;
    order_side_number: string;
    organization: string;
    product: ProductDetails;
    quantity: number;
    replaced_at: string | null;
    sent_at: string | null;
    serial_number: number | null;
    status: SimpleEnumType<ReturnZipsStatuses>;
  }

  export interface TransportCompany {
    id: number;
    name: string;
  }

  export interface UpdReturnZips {
    comment: string;
    transport_company: string;
    track_number: string;
    delivery_self_type: string,
    items: {id: number}[]
  }

  export interface CatalogProduct {
    [key: string]: unknown,
    id: number,
    category_name: string,
    product_code: string,
    nomenclature: string,
    price: number,
    software_type_name: string | null
  }

  export interface CatalogProductResponse extends ApiPagination {
    data: {
      items: CatalogProduct[],
      filters: {
        categories: Products.CategoryFilterItem[],
        software_types: Products.CatalogFilterItem[],
        equipment_models: Products.EquipmentModelFilterItem[]
      }
    }
  }

  export type CatalogProductRequestFilters = {
    [key: string]: unknown,
    search: string,
    categories: number[];
    software_type: number | null;
    equipment_models: number[],
    not_active_only: boolean
  }

  export type CatalogProductRequest = {
    filters: CatalogProductRequestFilters,
    filters_exist: boolean,
    pagination: RequestPagination,
  }
}
